import { Navigate, Outlet, useLocation } from 'react-router';

import { Container } from '@/components/ui/Container';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontPaths } from '@/features/storefront/paths';

import { useCustomerAuth } from '../hooks/useCustomerAuth';

/**
 * Gate for the signed-in half of the account.
 *
 * Waits for the boot refresh before deciding. Redirecting on `loading` would
 * bounce every shopper to the sign-in form on each hard reload, because the
 * access token only ever lives in memory and the session is recovered
 * asynchronously from the refresh cookie.
 *
 * Where they were going is carried in location state so sign-in can return
 * them to it - a shopper who followed "track my order" from an email should
 * land on that order, not on a generic overview.
 */
export function RequireCustomer() {
  const { status, leaving } = useCustomerAuth();
  const location = useLocation();
  const paths = useStorefrontPaths();
  const copy = useShopContent();

  if (status === 'loading') {
    return (
      <Container className="py-20 text-center text-sm">
        <p>{copy('account.account_loading')}</p>
      </Container>
    );
  }

  // The shopper signed out here and is already being taken home. Redirecting
  // now would race that navigation - an urgent render against a transition -
  // and win, leaving them on the sign-in form they did not ask for.
  if (status === 'anonymous' && leaving) return null;

  if (status === 'anonymous') {
    return (
      <Navigate
        to={paths.accountLogin()}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <Outlet />;
}
